import React, { useState, useCallback, useEffect, useContext, useMemo, useRef } from 'react';
import Video, { createLocalTracks } from 'twilio-video';
import { StyledConfrenceContainer } from './styles/ConferenceContainerStyled';
import { WebSocketContext } from '../../../_redux/actions/ActionSocket';
import { getConferenceIdFromURL } from '../../../_helpers';

import Lobby from './Lobby';
import Room from './Room';
import GuestJoin from './GuestJoin';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { LoadingContainer } from '../../../_elements/LoadingContainer';
import { conferenceApi } from '@/_services/api/conference';

const VideoChat = ({ match }) => {
    const ws = useContext(WebSocketContext);
    const user = useSelector((state) => state.user.user);
    const isLogged = useSelector((state) => state.user.isLogged);

    const [audio, setAudio] = useState(false);
    const [video, setVideo] = useState(false);
    const [room, setRoom] = useState(null);
    const [channelInfo, setChannelInfo] = useState({});
    const [connecting, setConnecting] = useState(false);
    const [participantInfo, setParticipantInfo] = useState({});
    const [allParticipantsInfo, setAllParticipantsInfo] = useState({});
    const [waitingParticipants, setWaitingParticipants] = useState([]);
    const conferenceId = match?.params?.meetId;
    const [host, setHost] = useState(null);
    const [error, setError] = useState('');
    const roomRef = useRef();
    const [waitingForHost, setWaitingForHost] = useState(false);
    const [recording, setRecording] = useState(false);

    const handleMuteLocalParticipant = (mutedParticipantId) => {
        const localUserId = user?.id || user?._id;
        const isLocalParticipant =
            localUserId && mutedParticipantId && localUserId.toString() === mutedParticipantId.toString();

        if (!isLocalParticipant || !roomRef.current) {
            return;
        }
        roomRef.current.localParticipant.audioTracks.forEach((publication) => {
            if (publication.track) {
                publication.track.disable();
            }
        });
        setAudio(false);
    };
    const [conferenceSettings, setConferenceSettings] = useState({});
    const updateConferenceSettings = (newSettings) => {
        setConferenceSettings((prevSettings) => ({
            ...prevSettings,
            ...newSettings,
        }));
    };

    useEffect(() => {
        if (!ws || !ws.socket) return;
        const socket = ws.socket;
        const handleNewParticipant = (data) => {
            setParticipantInfo((prev) => ({ ...prev, [data._id]: data }));
        };

        const handleAllParticipants = (data = []) => {
            const participantsMap = data.reduce((acc, participant) => {
                acc[participant.id] = participant;
                return acc;
            }, {});
            if (participantsMap && Object.keys(participantsMap).length > 0) {
                setAllParticipantsInfo(participantsMap);
            }
        };

        const handleWaitingParticipants = (data = []) => {
            setWaitingParticipants(Array.isArray(data) ? data : []);
        };

        const handleMutedParticipant = (data) => {
            setParticipantInfo((prev) => {
                if (!prev || !prev[data]) {
                    return prev;
                }
                return { ...prev, [data]: { ...prev[data], audio: false } };
            });
            handleMuteLocalParticipant(data);
        };

        const handleRemovedParticipant = (data) => {
            setParticipantInfo((prev) => {
                const tempParticipant = { ...prev };
                delete tempParticipant[data];
                return tempParticipant;
            });
        };

        const handleConferenceSettings = (data = {}) => {
            setConferenceSettings(data);
        };
        const handleConferenceRecording = (data = {}) => {
            setRecording(data?.isRecording || false);
        };
        const handleAllowedParticipant = ({ participantId, conferenceId: allowedConferenceId }) => {
            const currentConferenceId = getConferenceIdFromURL();
            if (currentConferenceId === allowedConferenceId && participantId === (user?.id || user?._id)) {
                if (waitingForHost) {
                    setWaitingForHost(false);
                    if (!room && !roomRef.current) {
                        handleJoinConference();
                    }
                }
            }
        };

        socket.on('new-participant', handleNewParticipant);
        socket.on('all-participants', handleAllParticipants);
        socket.on('waiting-participants', handleWaitingParticipants);
        socket.on('allowed-participant', handleAllowedParticipant);
        socket.on('muted-participant', handleMutedParticipant);
        socket.on('removed-participant', handleRemovedParticipant);
        socket.on('conference-settings', handleConferenceSettings);
        socket.on('conference-recording', handleConferenceRecording);

        return () => {
            socket?.off('new-participant', handleNewParticipant);
            socket?.off('all-participants', handleAllParticipants);
            socket?.off('waiting-participants', handleWaitingParticipants);
            socket.off('allowed-participant', handleAllowedParticipant);
            socket?.off('muted-participant', handleMutedParticipant);
            socket?.off('removed-participant', handleRemovedParticipant);
            socket?.off('conference-settings', handleConferenceSettings);
            socket?.off('conference-recording', handleConferenceRecording);
        };
    }, [ws, user, conferenceId, waitingForHost, room]);

    useEffect(() => {
        // Keep the ref in sync with the room state
        roomRef.current = room;
    }, [room]);

    const handleDisconnect = useCallback(() => {
        setRoom((prevRoom) => {
            if (prevRoom) {
                prevRoom.localParticipant.tracks.forEach((trackPub) => {
                    trackPub.track.stop();
                });
                prevRoom.disconnect();
            }
            return null;
        });
        roomRef.current = null;
        if (ws && ws.socket) {
            ws.socket.emit('leave-conference', conferenceId, (error, data) => {
                if (error) {
                    console.log(error, 'leave-conference');
                }
                if (user && user?.role === 'guest') {
                    localStorage.removeItem('user');
                    localStorage.removeItem('userRefresh');
                }
            });
        }
    }, [ws, conferenceId, user]);

    const handleEndCall = useCallback(() => {
        if (ws && ws.socket) {
            ws.socket.emit('end-conference', conferenceId, (error, data) => {
                if (error) {
                    console.log(error, 'end-conference');
                }
            });
        }
        window.close();
    }, [ws, conferenceId]);
    const handleRemoveParticipant = useCallback(
        (participantId) => {
            if (ws && ws.socket) {
                ws.socket.emit('remove-participant', conferenceId, participantId, (error, data) => {
                    if (error) {
                        console.log(error, 'remove-participant');
                    }
                });
            }
        },
        [ws, conferenceId]
    );

    const handleMuteParticipant = useCallback(
        (participantId) => {
            if (ws && ws.socket) {
                ws.socket.emit('mute-participant', conferenceId, participantId, (error, data) => {
                    if (error) {
                        console.log(error, 'mute-participant');
                    }
                });
            }
        },
        [ws, conferenceId]
    );

    const startConference = async (conferenceData, conferenceId) => {
        try {
            setConnecting(true);
            const localTracks = await createLocalTracks({
                audio: true,
                video: video ? { height: 720, frameRate: 24, width: 1280, name: 'camera' } : false,
            });

            const newRoom = await Video.connect(conferenceData.confrenceTokens.jwt, {
                name: conferenceId,
                dominantSpeaker: true,
                networkQuality: true,
                tracks: localTracks,
            });

            setRoom(newRoom);
            roomRef.current = newRoom;
            // Always mute audio on join
            if (newRoom?.localParticipant) {
                newRoom.localParticipant.audioTracks?.forEach((track) => {
                    if (track.track) {
                        track.track.disable();
                    }
                });
                setAudio(false);
            }

            newRoom.on('disconnected', (room, error) => {
                toast.info('Meeting Ended/Disconnected');
                handleDisconnect();
                setRoom(null);
                roomRef.current = null;
            });
        } catch (error) {
            console.error('ERROR WHILE STARTING CONFERENCE: ', error);
            toast.error('Failed to connect to conference. Please try again later');
        } finally {
            setConnecting(false);
        }
    };

    const [seconds, setSeconds] = useState(5);

    const handleJoinConference = async (event) => {
        try {
            if (!conferenceId || !ws || !ws.socket) {
                return null;
            }

            ws.socket.emit('join-conference', { conferenceId, audio, video }, async (err, data) => {
                if (err) {
                    setConnecting(false);
                    toast.error(err.message);
                    setError(err.message);
                    return;
                }
                console.log('CONFERENCE DATA => ', data);
                if (data?.status === 'guest-not-allowed') {
                    try {
                        await conferenceApi.guestRequest(conferenceId);
                    } catch (error) {
                        toast.error('Failed guest request');
                        return;
                    } finally {
                        setWaitingForHost(true);
                        return;
                    }
                }

                setHost(data?.conferenceData?.host);
                const _channelInfo = data?.conferenceData?.room;
                if (_channelInfo) {
                    _channelInfo.conferenceData = data?.conferenceData;
                }
                setChannelInfo(_channelInfo);
                const participantsMap = (data?.conferenceData?.allParticipantsInfo || []).reduce((acc, participant) => {
                    acc[participant.id] = participant;
                    return acc;
                }, {});
                if (participantsMap && Object.keys(participantsMap).length > 0) {
                    setAllParticipantsInfo(participantsMap);
                }
                setParticipantInfo(data?.participantList);
                setConferenceSettings(data?.conferenceData?.settings || {});
                setWaitingParticipants(data?.conferenceData?.waitingParticipants || []);
                setRecording(data?.conferenceData?.isRecording || false);
                await startConference(data, conferenceId);
            });
        } catch (e) {
            console.log(e, 'call error');
        }
    };

    const [permissionState, setPermissionState] = useState('Checking for permissions ...');

    useEffect(() => {
        (async () => {
            let isMicrophonePermissonDenied =
                (await navigator.permissions.query({ name: 'microphone' })).state === 'denied';
            let isVideoPermissionDenied = (await navigator.permissions.query({ name: 'camera' })).state === 'denied';

            if (isMicrophonePermissonDenied && isVideoPermissionDenied) {
                setPermissionState('Failed! Microphone and Camera permission is denied.');
                return toast.error(
                    'Oops! Microphone and Camera permissions are denied, Please allow Microphone and Camera permissions and Refresh this page'
                );
            }
            if (isMicrophonePermissonDenied) {
                setPermissionState('Failed! Microphone permission is denied.');
                return toast.error(
                    'Oops! Microphone permission denied, Please allow Microphone permission and Refresh this page'
                );
            }
            if (isVideoPermissionDenied) {
                setPermissionState('Failed! Camera permission is denied.');
                return toast.error(
                    'Oops! Camera permission denied, Please allow Camera permission and Refresh this page'
                );
            }
            setPermissionState('granted');
        })();
    }, []);

    useEffect(() => {
        if (!isLogged) return;
        const timer = setInterval(() => {
            if (seconds > 0) {
                setSeconds((prev) => prev - 1);
            }
        }, 1000);

        if (seconds <= 0) {
            console.log(seconds, 'seconds use effect for');
            handleJoinConference();
        }

        return () => {
            clearInterval(timer);
            setError('');
        };
    }, [seconds, isLogged]);

    useEffect(() => {
        return () => {
            if (roomRef.current) {
                handleDisconnect();
            }
        };
    }, []);

    const handleAudio = (e) => {
        const isAudioEnabled = e.target.checked;
        setAudio(isAudioEnabled);
    };

    const handleVideo = (e) => {
        setVideo(e.target.checked);
    };

    const handleRecording = (_recording) => {
        setRecording(_recording);
    }
    const conferenceParticipants = useMemo(() => {
        const result = {};
        for (const p of Object.values(participantInfo)) {
            result[p?._id || p?.id] = p;
        }
        return result;
    }, [participantInfo]);

    let render;
    if (waitingForHost) {
        render = <LoadingContainer text="Waiting for host to let you in..." />;
    } else if (room) {
        render = (
            <Room
                room={room}
                conferenceId={conferenceId}
                handleDisconnect={handleDisconnect}
                audio={audio}
                video={video}
                recording={recording}
                handleAudio={handleAudio}
                handleVideo={handleVideo}
                handleRecording={handleRecording}
                allParticipantsInfo={allParticipantsInfo}
                waitingParticipants={waitingParticipants}
                participantInfo={conferenceParticipants}
                channelInfo={channelInfo}
                conferenceHostId={host}
                handleEndCall={handleEndCall}
                handleMuteParticipant={handleMuteParticipant}
                handleRemoveParticipant={handleRemoveParticipant}
                conferenceSettings={conferenceSettings}
                updateConferenceSettings={updateConferenceSettings}
            />
        );
    } else {
        render = (
            <Lobby
                connecting={connecting}
                audio={audio}
                video={video}
                permissionState={permissionState}
                seconds={seconds}
                handleAudio={handleAudio}
                handleVideo={handleVideo}
                error={error}
            />
        );
    }

    if (isLogged && !user) {
        return <LoadingContainer text="Joining Meeting..." />;
    }

    if (!isLogged) {
        return <GuestJoin embedded={true} />;
    }

    return <StyledConfrenceContainer>{render}</StyledConfrenceContainer>;
};

export default VideoChat;
